import { useEffect, useState } from "react";
import { Action, Card, Pill } from "../../calm.tsx";
import { cx, Empty } from "../../ui.tsx";
import type { SkillListItemDTO } from "./skills.ts";

export type PickerAgent = { id: string; name: string };

/** Checkbox list of agents for one skill. Read-only (no checkboxes, no save) for non-admins. */
export function AgentPicker({ agents, selected, isAdmin, saving, onSave }: { agents: PickerAgent[]; selected: string[]; isAdmin: boolean; saving?: boolean; onSave: (agentIds: string[]) => void }) {
  const [draft, setDraft] = useState<string[] | null>(null);
  // The saved state arriving from the server replaces the draft; until then the ticks stay as the admin set them.
  const savedKey = selected.join(",");
  useEffect(() => setDraft(null), [savedKey]);
  const current = draft ?? selected;
  const dirty = draft !== null && (draft.length !== selected.length || draft.some((id) => !selected.includes(id)));
  if (!isAdmin) {
    const names = agents.filter((a) => selected.includes(a.id)).map((a) => a.name);
    return <p className="text-[12.5px] text-ink-2" data-testid="skill-agents-readonly">{names.length ? names.join(", ") : "No agents use this skill."}</p>;
  }
  return (
    <div data-testid="skill-agent-picker">
      <ul className="grid gap-1.5 sm:grid-cols-2">
        {agents.map((a) => (
          <li key={a.id}>
            <label className="flex cursor-pointer items-center gap-2 text-[13px]">
              <input
                type="checkbox"
                checked={current.includes(a.id)}
                onChange={(e) => setDraft(e.target.checked ? [...current, a.id] : current.filter((id) => id !== a.id))}
                data-testid={`skill-agent-${a.id}`}
              />
              {a.name}
            </label>
          </li>
        ))}
      </ul>
      <div className="mt-3">
        <Action primary disabled={!dirty || saving} onClick={() => onSave(current)} testId="skill-agents-save">
          Save agents
        </Action>
      </div>
    </div>
  );
}

export function SkillRow({
  skill, isAdmin, expanded, onToggleAgents, onReview, onRefetch, onArchive, busy, children,
}: {
  skill: SkillListItemDTO;
  isAdmin: boolean;
  expanded: boolean;
  onToggleAgents: () => void;
  onReview: (versionId: string) => void;
  onRefetch: () => void;
  onArchive: () => void;
  busy?: boolean;
  /** The agent picker, shown while the row is expanded. */
  children?: React.ReactNode;
}) {
  return (
    <li className="px-5 py-4" data-testid="skill-row" data-skill={skill.name}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[14px] font-medium" data-testid="skill-name">{skill.name}</span>
            <Pill tone={skill.approvedVersion ? "live" : "neutral"} testId="skill-version">{skill.approvedVersion ? `v${skill.approvedVersion} approved` : "not approved"}</Pill>
            {skill.pendingCount > 0 && <Pill tone="signal" testId="skill-pending">{skill.pendingCount} pending</Pill>}
          </div>
          <p className="mt-1 break-all font-mono text-[11.5px] text-ink-3" data-testid="skill-source">{skill.source}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={onToggleAgents} className="rounded-full bg-ink/[0.05] px-3 py-1.5 text-[12.5px] tabular-nums" aria-expanded={expanded} data-testid="skill-used-by">
            used by {skill.usedBy} {skill.usedBy === 1 ? "agent" : "agents"}
          </button>
          {skill.pendingVersionId && (
            <Action onClick={() => onReview(skill.pendingVersionId!)} testId="skill-review">Review</Action>
          )}
          {isAdmin && (
            <>
              <Action disabled={busy} onClick={onRefetch} testId="skill-refetch">Check for update</Action>
              <Action danger disabled={busy} onClick={onArchive} testId="skill-archive">Archive</Action>
            </>
          )}
        </div>
      </div>
      {expanded && <div className={cx("mt-4 rounded-[14px] bg-ink/[0.035] p-4")}>{children}</div>}
    </li>
  );
}

export function SkillLibrary({ skills, children }: { skills: SkillListItemDTO[]; children?: React.ReactNode }) {
  if (!skills.length) return <Empty>No skills in the library yet.</Empty>;
  return (
    <Card pad={false}>
      <ul className="divide-y divide-rule" data-testid="skill-list">{children}</ul>
    </Card>
  );
}
